/**
 * API Client Provider
 * 
 * This provider configures the apiClient with a token getter function
 * that can fetch fresh tokens from the Logto SDK.
 */
import React, { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../auth/useAuth';
import { setTokenGetter } from '../services/apiClient';
import { useAuthStore } from '../stores/authStore';
import { isTokenExpired } from '../utils/jwtHelper';

interface ApiClientProviderProps {
  children: React.ReactNode;
}

export const ApiClientProvider: React.FC<ApiClientProviderProps> = ({ children }) => {
  const { getAccessToken, getIdToken, isAuthenticated } = useAuth();

  // Use ref to avoid stale closure issues
  const getAccessTokenRef = useRef(getAccessToken);
  const getIdTokenRef = useRef(getIdToken);
  getAccessTokenRef.current = getAccessToken;
  getIdTokenRef.current = getIdToken;

  // Create a stable token getter function that tries multiple sources
  const tokenGetter = useCallback(async (): Promise<string | null> => {
    // Logto's getAccessToken triggers a silent refresh via the refresh_token
    // when the cached access token has expired. Calling it first gives us
    // an up-to-date session before we read the ID token (which is what the
    // backend expects). Without this, getIdToken just returns the stale
    // cached value and the user is bumped on the next 401.
    try {
      await getAccessTokenRef.current();

      const idToken = await getIdTokenRef.current();
      if (idToken && !isTokenExpired(idToken)) {
        return idToken;
      }

      const accessToken = await getAccessTokenRef.current();
      if (accessToken && !isTokenExpired(accessToken)) {
        return accessToken;
      }
    } catch (error) {
      console.error('Error getting token from Logto:', error);
    }

    // Fallback to stored token in authStore (race conditions just after callback).
    const storedToken = useAuthStore.getState().accessToken;
    if (storedToken && !isTokenExpired(storedToken)) {
      return storedToken;
    }

    // No usable token available. Let apiClient decide whether to surface
    // a hard logout — don't pre-empt that decision here.
    return null;
  }, []);

  // Always set the token getter on mount - let it handle auth state internally
  useEffect(() => {
    setTokenGetter(tokenGetter);

    // Cleanup on unmount
    return () => {
      setTokenGetter(null);
    };
  }, [tokenGetter, isAuthenticated]);

  return <>{children}</>;
};

export default ApiClientProvider;
